import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import { useConsultation } from '../../../../context/ConsultationContext';
import { handleTreatmentServiceNavigation } from '../components/treatmentNavigationHelper';
import styles from './css/BloodPressure.module.css';
import { Send } from 'lucide-react';

const BloodPressure = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consultationData, updateConsultationData, getStepDraft, setStepDraft, goToStep } = useConsultation();

    const patient = location?.state?.patient || consultationData?.patient || {};
    const initials = `${patient?.firstname?.[0] || ""}${patient?.surname?.[0] || ""}`.toUpperCase() || "TN";
    const patientName = `${patient?.firstname || ""} ${patient?.surname || ""}`.trim() || initials;
    const profileNumber = patient?.profilenumber || "448896";

    const [systolic, setSystolic] = useState('');
    const [diastolic, setDiastolic] = useState('');
    const [pulse, setPulse] = useState('');
    const [clinicalNotes, setClinicalNotes] = useState('');
    const [hypertensionHistory, setHypertensionHistory] = useState(false);

    const [symptoms, setSymptoms] = useState({
        headache: false,
        dizziness: false,
        chestPain: false
    });

    const handleSymptomChange = (symptom) => {
        setSymptoms(prev => ({
            ...prev,
            [symptom]: !prev[symptom]
        }));
    };

    const handleInvoice = () => {
        handleTreatmentServiceNavigation({
            location,
            navigate,
            consultationData,
            updateConsultationData,
            getStepDraft,
            setStepDraft,
            goToStep,
            serviceData: {
                blood_pressure: {
                    systolic,
                    diastolic,
                    pulse,
                    clinicalNotes,
                    hypertensionHistory,
                    symptoms,
                },
            },
        });
    };

    useEffect(() => {
        document.body.classList.add(styles.hideSidebar);
        return () => {
            document.body.classList.remove(styles.hideSidebar);
        };
    }, []);

    return (
        <div className={styles.bloodPressureContainer}>
            <Navbar />

            <main className={styles.mainContent}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <div className={styles.headerIcon} aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                        </div>
                        <h1 className={styles.title}>Blood Pressure Test</h1>
                    </div>

                    <div className={styles.patientCard}>
                        <div className={styles.avatar} aria-hidden="true">{initials || 'TN'}</div>
                        <div className={styles.patientText}>
                            <div className={styles.patientName}>{patientName || 'Thandiwe Ndlovu'}</div>
                            <div className={styles.patientMetaRow}>
                                <span className={styles.profileInfo}>Profile: {profileNumber || '448896'}</span>
                                <span className={styles.dependentBadge}>Dependent</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>READINGS</div>
                        <div className={styles.readingsRow}>
                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Systolic</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={systolic}
                                        onChange={(e) => setSystolic(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='120'
                                    />
                                    <span className={styles.unit}>mmHg</span>
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Diastolic</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={diastolic}
                                        onChange={(e) => setDiastolic(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='80'
                                    />
                                    <span className={styles.unit}>mmHg</span>
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Pulse</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={pulse}
                                        onChange={(e) => setPulse(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='72'
                                    />
                                    <span className={styles.unit}>bpm</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>SYMPTOMS &amp; HISTORY</div>

                        <div className={styles.symptomsList}>
                            <div className={styles.checkboxItem}>
                                <Checkbox
                                    id="headache"
                                    checked={symptoms.headache}
                                    onChange={() => handleSymptomChange('headache')}
                                />
                                <label htmlFor="headache" className={styles.checkboxLabel}>Headache</label>
                            </div>

                            <div className={styles.checkboxItem}>
                                <Checkbox
                                    id="dizziness"
                                    checked={symptoms.dizziness}
                                    onChange={() => handleSymptomChange('dizziness')}
                                />
                                <label htmlFor="dizziness" className={styles.checkboxLabel}>Dizziness</label>
                            </div>

                            <div className={styles.checkboxItem}>
                                <Checkbox
                                    id="chestPain"
                                    checked={symptoms.chestPain}
                                    onChange={() => handleSymptomChange('chestPain')}
                                />
                                <label htmlFor="chestPain" className={styles.checkboxLabel}>Chest pain</label>
                            </div>
                        </div>

                        <div className={styles.toggleSection}>
                            <label className={styles.toggleLabel}>Hypertension history</label>
                            <button
                                type="button"
                                className={`${styles.toggleButton} ${hypertensionHistory ? styles.active : ''}`}
                                onClick={() => setHypertensionHistory(!hypertensionHistory)}
                                aria-pressed={hypertensionHistory}
                            >
                                <div className={styles.toggleSlider}></div>
                            </button>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.fieldLabel}>Clinical Notes</div>
                        <textarea
                            value={clinicalNotes}
                            onChange={(e) => setClinicalNotes(e.target.value)}
                            className={styles.clinicalNotes}
                            placeholder="Enter clinical notes..."
                            rows={6}
                        />
                    </div>

                    <div className={styles.cardFooter}>
                        <Button
                            onClick={handleInvoice}
                            className={styles.invoiceButton}
                            type="button"
                        >
                            <Send size={16} />
                            Invoice
                        </Button>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default BloodPressure;